/**
 * Bounding-box helpers for viewport culling of map shapes, lifted from
 * Components/MapWithToolbar so the mission map can share them.
 */

const BBOX_KEY = '__bbox'

/**
 * The lat/lng box around a polygon's path, cached on the item itself (as a
 * non-enumerable property) so it is computed once per shape however often
 * the map pans.
 * @param {{paths?: Array<{lat?: number, lng?: number, latitude?: number, longitude?: number}>}} item
 * @returns {?{north: number, south: number, east: number, west: number}}
 */
export const getItemBBox = item => {
  if (!item) return null
  if (item[BBOX_KEY]) return item[BBOX_KEY]
  const paths = item.paths || item.boundary
  if (!Array.isArray(paths) || paths.length === 0) return null
  let south = Infinity
  let north = -Infinity
  let west = Infinity
  let east = -Infinity
  for (const point of paths) {
    const lat = point && (point.lat !== undefined ? point.lat : point.latitude)
    const lng = point && (point.lng !== undefined ? point.lng : point.longitude)
    if (typeof lat !== 'number' || typeof lng !== 'number') continue
    if (lat < south) south = lat
    if (lat > north) north = lat
    if (lng < west) west = lng
    if (lng > east) east = lng
  }
  if (south === Infinity) return null
  const bbox = { south, north, west, east }
  Object.defineProperty(item, BBOX_KEY, {
    value: bbox,
    enumerable: false,
    configurable: true,
  })
  return bbox
}

/**
 * @param {?object} bbox
 * @param {?object} viewport
 * @returns {boolean} true when they overlap (or either is unknown)
 */
export const bboxIntersectsViewport = (bbox, viewport) => {
  if (!bbox || !viewport) return true
  if (bbox.north < viewport.south) return false
  if (bbox.south > viewport.north) return false
  if (bbox.east < viewport.west) return false
  if (bbox.west > viewport.east) return false
  return true
}

/**
 * The viewport of a google map, inflated by `pad` (fraction) on each side so
 * shapes just outside the visible area are already drawn when the user pans.
 * @param {google.maps.Map} map
 * @param {number} [pad]
 * @returns {?{north: number, south: number, east: number, west: number}}
 */
export const viewportOf = (map, pad = 0.25) => {
  const bounds = map && map.getBounds && map.getBounds()
  if (!bounds) return null
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  const latPad = (ne.lat() - sw.lat()) * pad
  const lngPad = (ne.lng() - sw.lng()) * pad
  return {
    north: ne.lat() + latPad,
    south: sw.lat() - latPad,
    east: ne.lng() + lngPad,
    west: sw.lng() - lngPad,
  }
}

/**
 * The API's bbox input for a viewport.
 * @param {?object} viewport
 * @returns {?{minLat: number, minLng: number, maxLat: number, maxLng: number}}
 */
export const viewportToBBoxInput = viewport =>
  viewport
    ? {
        minLat: viewport.south,
        minLng: viewport.west,
        maxLat: viewport.north,
        maxLng: viewport.east,
      }
    : null

/**
 * The centre of a set of points, for fitting a map to a selection.
 * @param {Array<{latitude: number, longitude: number}>} points
 * @returns {?{lat: number, lng: number}}
 */
export const centroidOf = points => {
  const valid = (points || []).filter(
    point =>
      point &&
      typeof point.latitude === 'number' &&
      typeof point.longitude === 'number',
  )
  if (valid.length === 0) return null
  const sum = valid.reduce(
    (acc, point) => ({
      lat: acc.lat + point.latitude,
      lng: acc.lng + point.longitude,
    }),
    { lat: 0, lng: 0 },
  )
  return { lat: sum.lat / valid.length, lng: sum.lng / valid.length }
}
